import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { mediaUrl } from "@/lib/api";

/**
 * "Choose Your Experience" - 5 large interactive rows on ivory bg.
 * Hovering a row reveals a cinematic preview image on the right.
 */
export default function ChooseExperience({ services = [] }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = services[activeIdx];

  return (
    <section
      data-testid="choose-experience"
      className="bg-ivory text-ink py-24 md:py-40 relative overflow-hidden"
    >
      <div className="mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 md:col-span-5 md:sticky md:top-32">
            <p className="label-mono opacity-60 line-badge">02 — The Work</p>
            <h2 className="mt-8 font-editorial text-5xl md:text-7xl leading-[0.95]">
              Choose your <em className="italic">experience</em>.
            </h2>
            <p className="mt-8 max-w-md text-ink/70 text-lg leading-relaxed">
              Five bodies of work, each with its own tone. Every session is directed personally by
              Elvis Sereo.
            </p>

            <div className="hidden md:block relative mt-14 aspect-[4/5] w-full max-w-md">
              <AnimatePresence mode="wait">
                {active && (
                  <motion.img
                    key={active.slug}
                    src={mediaUrl(active.tile_image || active.hero_image)}
                    alt={active.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.02 }}
                    transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
                  />
                )}
              </AnimatePresence>
            </div>
          </div>

          <ul className="col-span-12 md:col-span-7 border-t border-ink/15">
            {services.map((svc, i) => (
              <li
                key={svc.slug}
                onMouseEnter={() => setActiveIdx(i)}
                className={`group border-b border-ink/15 transition-colors duration-500 ${
                  activeIdx === i ? "opacity-100" : "opacity-50"
                }`}
              >
                <Link
                  to={`/services/${svc.slug}`}
                  className="flex items-center justify-between py-8 md:py-10"
                  data-testid={`experience-tile-${svc.slug}`}
                >
                  <div className="flex items-baseline gap-6">
                    <span className="label-mono text-ink/50">
                      0{i + 1}
                    </span>
                    <h3 className="font-editorial text-4xl md:text-6xl leading-none">
                      {svc.name}
                    </h3>
                  </div>
                  <motion.span
                    className="hidden md:inline-block label-mono"
                    animate={{ x: activeIdx === i ? 0 : -12, opacity: activeIdx === i ? 1 : 0.3 }}
                    transition={{ duration: 0.5 }}
                  >
                    View →
                  </motion.span>
                </Link>
                {/* Mobile preview inline */}
                {activeIdx === i && (
                  <div className="md:hidden pb-8">
                    <div className="aspect-[16/10] w-full overflow-hidden">
                      <img
                        src={mediaUrl(svc.tile_image || svc.hero_image)}
                        alt={svc.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
